import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, CheckCircle, Hammer, Ruler, PaintBucket, Lightbulb, ShieldCheck } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const etapes = [
  { icon: Lightbulb, title: "1. Visite et relevé", desc: "Je viens chez vous, je mesure, je photographie, je note ce qui coince au quotidien. On parle budget dès ce rendez-vous, ça évite de dessiner à côté." },
  { icon: Ruler, title: "2. Plans et 3D", desc: "Deux propositions d'aménagement, puis les plans cotés, la palette de teintes et les rendus 3D. Vous validez pièce par pièce, on ajuste avant de commander." },
  { icon: Hammer, title: "3. Chantier", desc: "Consultation des artisans, comparaison des devis, planning, visites régulières. Je réponds aux questions techniques à votre place." },
  { icon: ShieldCheck, title: "4. Réception", desc: "On fait le tour ensemble, liste des reprises à l'appui et on ne solde qu'une fois les réserves levées." },
];

const typesRenovation = [
  { title: "Rénovation complète", desc: "Appartement ou maison repris de fond en comble : distribution, électricité, sols, menuiseries. Un fil conducteur d'une pièce à l'autre, sans que tout se ressemble." },
  { title: "Cuisine, salle de bain, salon", desc: "Les pièces où l'on passe le plus de temps et celles où une erreur de plan se paie tous les jours. Implantation, hauteurs, rangements, éclairage, matériaux qui vieillissent bien." },
  { title: "Suite parentale & dressing", desc: "Gagner une salle d'eau dans une chambre, caser un dressing sous rampant, garder la lumière. Souvent une question de dix centimètres bien placés." },
  { title: "Réaménagement sans gros œuvre", desc: "Ouvrir une circulation, déplacer une porte, exploiter un couloir perdu. Beaucoup de logements lyonnais gagnent une pièce sans toucher aux murs porteurs." },
];

interface FeaturedProject {
  id: string; title: string; slug: string; cover_image: string | null; location: string | null;
}

const RenovationInterieureLyon = () => {
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  useEffect(() => {
    supabase.from("projects").select("id, title, slug, cover_image, location")
      .eq("published", true).order("display_order").limit(4)
      .then(({ data }) => setProjects(data ?? []));
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Rénovation intérieure Lyon & Ouest Lyonnais | JIIA Studio"
        description="Rénovation d'appartement et maison à Lyon. JIIA Studio vous accompagne de la conception au suivi de chantier : cuisine, salle de bain, réaménagement complet. Devis gratuit."
        canonical="/renovation-interieure-lyon"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Rénovation intérieure Lyon",
            "provider": { "@type": "LocalBusiness", "name": "JIIA Studio", "url": "https://jiia-studio.com" },
            "areaServed": [{ "@type": "City", "name": "Lyon" }, { "@type": "AdministrativeArea", "name": "Rhône-Alpes" }],
            "serviceType": "Rénovation intérieure",
            "description": "Rénovation d'appartement et maison à Lyon et Ouest Lyonnais. Conception, plans, suivi de chantier."
          }
        ]}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Rénovation intérieure<br />
              <span className="italic">Lyon, agglomération et Rhône-Alpes</span>
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl leading-relaxed mb-4">
              Rénover, c'est rarement tout casser. C'est souvent déplacer deux cloisons et revoir la lumière.
            </p>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-4">
              J'interviens sur des appartements et des maisons à Lyon, dans l'Ouest lyonnais et plus largement en Rhône-Alpes : immeubles anciens de la Croix-Rousse, appartements des années 60 à Villeurbanne, maisons de famille à Écully ou Tassin. Chaque bâti a ses habitudes, ses hauteurs sous plafond et ses mauvaises surprises.
            </p>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-4">
              Salle de bain, cuisine, suite parentale ou logement entier : je travaille les plans, les teintes et les matières, puis je reste jusqu'à la fin du chantier.
            </p>
            <p className="text-foreground font-body font-medium max-w-2xl mb-2">
              De la conception, en passant par la 3D jusqu'au suivi de chantier.
            </p>
            <p className="text-foreground font-body font-medium max-w-2xl mb-8">
              Premier rendez-vous offert.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Devis gratuit <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir mes réalisations
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Types de rénovation */}
      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Les espaces sur lesquels j'interviens
            </h2>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-12">
              Une pièce ou cent-vingt mètres carrés, la méthode ne change pas, seule l'ampleur du chantier varie.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {typesRenovation.map((type, i) => (
              <AnimatedSection key={type.title} delay={i * 0.1}>
                <div className="p-6 border border-border rounded-sm hover:border-accent/50 transition-colors h-full">
                  <PaintBucket className="h-5 w-5 text-accent mb-3" />
                  <h3 className="font-heading text-lg mb-2">{type.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{type.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Étapes */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Méthode</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              De la première idée au jour de la livraison
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {etapes.map((etape, i) => (
              <AnimatedSection key={etape.title} delay={i * 0.15}>
                <div className="text-center">
                  <etape.icon className="h-8 w-8 text-accent mx-auto mb-4" />
                  <h3 className="font-heading text-lg mb-2">{etape.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{etape.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Projets */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Réalisations</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Mes projets de rénovation
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[16/10] overflow-hidden rounded-sm mb-3">
                      <img src={project.cover_image || ""} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{project.title}</h3>
                    {project.location && <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {project.location}</p>}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/projets" className="inline-flex items-center gap-2 text-sm font-body text-accent hover:text-accent/80 transition-colors uppercase tracking-widest">
                Voir tous les projets <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pourquoi un architecte */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container max-w-3xl">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-6">
              Pourquoi faire appel à un architecte d'intérieur pour votre rénovation ?
            </h2>
            <div className="space-y-4 text-muted-foreground font-body leading-relaxed">
              <p>
                Un plaquiste pose ce qu'on lui demande, un cuisiniste vend sa cuisine, un carreleur
                carrèle. Personne, dans cette chaîne, n'a pour mission de regarder l'ensemble :
                <strong> la circulation, la lumière du matin, l'endroit où poser les manteaux</strong>.
                C'est exactement le trou que je viens combler.
              </p>
              <p>
                Concrètement, je consulte les artisans, je compare les devis ligne à ligne et je passe
                sur le chantier. Quand le maçon découvre une poutre non prévue un mardi matin,
                c'est moi qui tranche, pas vous depuis votre bureau.
              </p>
              <p>
                Les <Link to="/prestations" className="text-accent underline underline-offset-4">visualisations 3D</Link> servent
                à ça aussi : voir la crédence en zellige à côté du plan de travail avant de la commander,
                plutôt que le jour de la pose.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Racontez-moi votre logement
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Surface, ce qui ne va pas, ce que vous aimeriez. Premier rendez-vous offert, sans engagement, réponse sous 48h.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Demander un devis gratuit <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default RenovationInterieureLyon;
